import { Box, Link as MuiLink, Skeleton, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { supportApi } from "../client.js";
import { SupportErrorState } from "../components/SupportEmptyState.js";
import { PageLoader } from "@/components/ui/page-loader";

import { AssignMenu } from "./components/AssignMenu.js";
import { AttachmentsPanel } from "./components/AttachmentsPanel.js";
import { CommentForm } from "./components/CommentForm.js";
import { CommentList } from "./components/CommentList.js";
import { DetailRow, Empty, Section, TimeStamp } from "./components/detailLayout.js";
import { PriorityMenu } from "./components/PriorityMenu.js";
import { SlaPanel } from "./components/SlaPanel.js";
import { TicketHeader } from "./components/TicketHeader.js";
import { TransitionMenu } from "./components/TransitionMenu.js";
import { WatcherMenu } from "./components/WatcherMenu.js";

/**
 * Jira-style ticket detail. Two columns: the conversation (description,
 * attachments, activity) on the left, the Details rail (status / priority /
 * assignee / reporter / org / timestamps) + staff SLA + watcher widgets on the
 * right (sticky on desktop).
 *
 * This file is the data + composition orchestrator — the six reads, the four
 * mutations, and the two-column layout. The title row lives in `TicketHeader`,
 * the attachment grid in `AttachmentsPanel`, the section/row/time primitives in
 * `detailLayout`, and the interactive menus + forms in the other `components/`
 * modules.
 */
export function TicketDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const ticketQuery = useQuery({
    queryKey: ["support", "ticket", id],
    queryFn: () => supportApi.getTicket(id!),
    enabled: !!id,
  });
  // The admin detail brings the staff-only adornments — per-ticket SLA
  // override + watcher list — that the new <SlaPanel> and
  // <WatcherMenu> mount on. We fetch it alongside the legacy ticket
  // shape so the rest of the page keeps using its existing field
  // names; only the two new widgets read from `adminTicketQuery`.
  const adminTicketQuery = useQuery({
    queryKey: ["support", "admin-ticket", id],
    queryFn: () => supportApi.getAdminTicket(id!),
    enabled: !!id,
  });
  const commentsQuery = useQuery({
    queryKey: ["support", "ticket", id, "comments"],
    queryFn: () => supportApi.listComments(id!),
    enabled: !!id,
  });
  const statusesQuery = useQuery({
    queryKey: ["support", "statuses"],
    queryFn: supportApi.listStatuses,
    // 30 s — short enough that backend status changes (or a bug-fix
    // landing on the Rails side) flow through without hitting a stale
    // cache; long enough to avoid re-fetching on every page nav.
    staleTime: 30_000,
  });
  const assigneesQuery = useQuery({
    queryKey: ["support", "assignable"],
    queryFn: () => supportApi.listAssignableUsers(),
    staleTime: 30_000,
  });

  // Comment posting now lives entirely inside `<CommentForm>` — it
  // owns the admin POST + per-comment file upload via
  // `supportApi.postAdminTicketComment` so the staff-only
  // `is_internal` toggle round-trips through the admin namespace. The
  // detail page just invalidates the comments query when a new row
  // lands. See `./components/CommentForm.tsx`.

  const transition = useMutation({
    mutationFn: (statusId: number) =>
      supportApi.transition(id!, { ticket_status_id: statusId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support", "ticket", id] });
      qc.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
  });

  const assign = useMutation({
    mutationFn: (assigneeId: number | null) =>
      supportApi.assign(id!, { assignee_id: assigneeId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support", "ticket", id] });
      qc.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
  });

  // Priority change goes through the dedicated admin endpoint
  // (`PATCH /api/admin/tickets/:id/priority`) so the Rails
  // `Tickets::UpdatePriority` service runs — that's where history +
  // audit + customer-activity notification all fire from.
  const setPriority = useMutation({
    mutationFn: (priorityId: number) =>
      supportApi.updateAdminTicketPriority(id!, { priority_id: priorityId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["support", "ticket", id] });
      qc.invalidateQueries({ queryKey: ["support", "admin-ticket", id] });
      qc.invalidateQueries({ queryKey: ["support", "tickets"] });
    },
  });

  const prioritiesQuery = useQuery({
    queryKey: ["support", "priorities"],
    queryFn: supportApi.listPriorities,
    staleTime: 30_000,
  });

  const attachmentsQuery = useQuery({
    queryKey: ["support", "ticket", id, "attachments"],
    queryFn: () => supportApi.listAttachments(id!),
    enabled: !!id,
  });

  const upload = useMutation({
    mutationFn: (files: File[]) => supportApi.uploadAttachments(id!, files),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["support", "ticket", id, "attachments"] }),
  });

  const t = ticketQuery.data?.data;

  return (
    // Extra top padding so the title row breathes a little under the
    // tab strip — the rest of the page uses `gap: 2` for inter-section
    // rhythm.
    <Box sx={{ display: "grid", gap: 2, pt: 2 }}>
      {ticketQuery.isLoading && <PageLoader />}
      {ticketQuery.isError && (
        <SupportErrorState error={ticketQuery.error} resourceLabel="ticket" />
      )}

      {t && (
        <>
          <TicketHeader ticket={t} />

          {/* ── Two-column body. Right rail is wider (360px) and starts
           * at the same y-position as Description because the header is
           * now above the grid. */}
          <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { lg: "minmax(0,1fr) 360px" }, alignItems: { lg: "start" } }}>
            <Box sx={{ display: "grid", minWidth: 0, gap: 1.5 }}>
              {t.description && (
                <Section title="Description">
                  <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6, color: "text.primary" }}>
                    {t.description}
                  </Typography>
                </Section>
              )}

              <Section
                title="Attachments"
                {...(attachmentsQuery.data
                  ? { count: attachmentsQuery.data.data.length }
                  : {})}
              >
                <AttachmentsPanel
                  attachments={attachmentsQuery.data?.data ?? []}
                  onUpload={(files) => upload.mutate(files)}
                  isUploading={upload.isPending}
                  error={
                    upload.error instanceof Error ? upload.error.message : null
                  }
                />
              </Section>

              <Section
                title="Activity"
                {...(commentsQuery.data
                  ? { count: commentsQuery.data.data.length }
                  : {})}
              >
                {commentsQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
                {commentsQuery.data && (
                  <CommentList comments={commentsQuery.data.data} />
                )}
                <Box sx={{ mt: 2, borderTop: 1, borderColor: "divider", pt: 2 }}>
                  <CommentForm
                    ticketId={id!}
                    onPosted={() => {
                      qc.invalidateQueries({
                        queryKey: ["support", "ticket", id, "comments"],
                      });
                      qc.invalidateQueries({
                        queryKey: ["support", "ticket", id, "attachments"],
                      });
                    }}
                  />
                </Box>
              </Section>
            </Box>

            {/* Right rail (sticky on desktop). Status was moved to
             * the title row, so this panel only carries the
             * read/lookup attributes — Priority, Assignee, Reporter,
             * Org, Created, Updated. */}
            <Box component="aside" sx={{ display: "grid", gap: 1.5, position: { lg: "sticky" }, top: { lg: 16 } }}>
              <Section title="Details" tight>
                <Box component="dl" sx={{ display: "grid", m: 0 }}>
                  <DetailRow label="Status">
                    {/* The canonical place to change status — click
                     * the pill to open every defined status. Title-row
                     * badge is read-only and mirrors this state. */}
                    <TransitionMenu
                      current={{
                        id: t.ticket_status.id,
                        name: t.ticket_status.name,
                        color: t.ticket_status.color ?? null,
                      }}
                      options={statusesQuery.data?.data ?? []}
                      onTransition={(sid) => transition.mutate(sid)}
                      isPending={transition.isPending}
                    />
                  </DetailRow>
                  <DetailRow label="Priority">
                    <PriorityMenu
                      current={
                        t.priority
                          ? {
                              id: t.priority.id,
                              name: t.priority.name,
                              color: t.priority.color ?? null,
                            }
                          : null
                      }
                      options={prioritiesQuery.data?.data ?? []}
                      onChange={(pid) => setPriority.mutate(pid)}
                      isPending={setPriority.isPending}
                    />
                  </DetailRow>
                  <DetailRow label="Assignee">
                    <AssignMenu
                      current={
                        t.assignee
                          ? { id: t.assignee.id, name: t.assignee.name }
                          : null
                      }
                      candidates={assigneesQuery.data?.data ?? []}
                      onAssign={(uid) => assign.mutate(uid)}
                      isPending={assign.isPending}
                    />
                  </DetailRow>
                  <DetailRow label="Reporter">
                    <Typography component="span" sx={{ fontSize: 14, color: "text.primary" }}>
                      {t.reporter?.name ?? <Empty />}
                    </Typography>
                  </DetailRow>
                  <DetailRow label="Organization">
                    {t.organization ? (
                      <MuiLink
                        component={Link}
                        to={`/support/organizations/${t.organization.id}`}
                        color="inherit"
                        underline="hover"
                        sx={{ fontSize: 14 }}
                      >
                        {t.organization.name}
                      </MuiLink>
                    ) : (
                      <Empty />
                    )}
                  </DetailRow>
                  <DetailRow label="Created">
                    <TimeStamp iso={t.created_at} />
                  </DetailRow>
                  <DetailRow label="Updated">
                    <TimeStamp iso={t.updated_at} />
                  </DetailRow>
                </Box>
              </Section>

              {/* Staff-only adornments — mounted symmetrically below
               * the Details card so the right rail reads top-to-bottom
               * as: lookups → SLA dates → watcher list. Both widgets
               * key off the admin show endpoint; while it's loading we
               * render a skeleton placeholder rather than the empty
               * state to avoid a flicker. */}
              {adminTicketQuery.isLoading && <Skeleton variant="rounded" sx={{ height: 120 }} />}
              {adminTicketQuery.data && (
                <SlaPanel
                  ticketId={id!}
                  initial={adminTicketQuery.data.data.ticket_sla ?? null}
                  onSaved={() => {
                    qc.invalidateQueries({
                      queryKey: ["support", "admin-ticket", id],
                    });
                    qc.invalidateQueries({
                      queryKey: ["support", "tickets"],
                    });
                  }}
                />
              )}
              {adminTicketQuery.data && (
                <WatcherMenu
                  ticketId={id!}
                  watchers={adminTicketQuery.data.data.watchers ?? []}
                  onChange={() => {
                    qc.invalidateQueries({
                      queryKey: ["support", "admin-ticket", id],
                    });
                  }}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}
